// src/constants/roles.js
// Single source of truth for user roles and capabilities on the client.
// Keep this in sync with server/src/constants/roles.js.

export const ROLES = {
  PLATFORM_ADMIN: "platform_admin",
  USER_ADMIN: "user_admin",
  CONTRIBUTOR: "contributor",
  ANALYST: "analyst",
};

export const ALL_ROLES = [
  ROLES.PLATFORM_ADMIN,
  ROLES.USER_ADMIN,
  ROLES.CONTRIBUTOR,
  ROLES.ANALYST,
];

// Legacy role value (older accounts / JWTs). Treated as Platform Admin.
export const LEGACY_ADMIN = "admin";

export const ROLE_LABELS = {
  [ROLES.PLATFORM_ADMIN]: "Platform Admin",
  [ROLES.USER_ADMIN]: "User Administrator",
  [ROLES.CONTRIBUTOR]: "Contributor",
  [ROLES.ANALYST]: "Analyst",
};

export const ROLE_OPTIONS = ALL_ROLES.map((value) => ({
  value,
  label: ROLE_LABELS[value],
}));

function normalize(role) {
  const r = String(role || "").trim().toLowerCase();
  return r === LEGACY_ADMIN ? ROLES.PLATFORM_ADMIN : r;
}

/** Friendly label for a (possibly legacy/unknown) role value. */
export function roleLabel(role) {
  return ROLE_LABELS[normalize(role)] || "Analyst";
}

/** A CSS-safe class fragment for the (normalized) role. */
export function roleClass(role) {
  return normalize(role) || ROLES.ANALYST;
}

/* ----- Capabilities (mirror the server) ----- */

export function isPlatformAdmin(role) {
  return normalize(role) === ROLES.PLATFORM_ADMIN;
}

export function canManageUsers(role) {
  const r = normalize(role);
  return r === ROLES.PLATFORM_ADMIN || r === ROLES.USER_ADMIN;
}

export function canManageLists(role) {
  const r = normalize(role);
  return r === ROLES.PLATFORM_ADMIN || r === ROLES.CONTRIBUTOR;
}

export function canAccessSettings(role) {
  return canManageUsers(role) || canManageLists(role) || isPlatformAdmin(role);
}

/** Roles the actor is allowed to assign (used to filter the dropdown).
    User Administrators can't grant Platform Admin. */
export function assignableRoles(actorRole) {
  if (isPlatformAdmin(actorRole)) return ALL_ROLES;
  if (canManageUsers(actorRole)) {
    return ALL_ROLES.filter((r) => r !== ROLES.PLATFORM_ADMIN);
  }
  return [];
}

/** Whether the actor may act on a user currently holding `targetRole`. */
export function canModifyUser(actorRole, targetRole) {
  if (isPlatformAdmin(actorRole)) return true;
  if (canManageUsers(actorRole)) return !isPlatformAdmin(targetRole);
  return false;
}
